import { TEAMS, type PartyPlayer, type PartyRoundReveal, type TeamIndex } from './party';

/** A team's emoji and (possibly renamed) name, e.g. "☀️ Team Sun". */
export function teamLabel(team: TeamIndex, names: readonly [string, string]): string {
  return `${TEAMS[team].emoji} ${names[team]}`;
}

export interface TeamStanding {
  team: TeamIndex;
  players: PartyPlayer[];
  /** Sum over the rounds of the team's average points. */
  total: number;
  /** The team's average in the latest round, null if none of its players played it. */
  lastRound: number | null;
}

/**
 * Team scores, best first. Each round a team scores the average of its players' points, so
 * a bigger team has no edge. Players who left no longer count.
 */
export function teamStandings(players: PartyPlayer[], history: PartyRoundReveal[]): TeamStanding[] {
  return ([0, 1] as const)
    .map((team) => {
      const members = players.filter((player) => player.team === team);
      const ids = new Set(members.map((member) => member.id));
      const averages = history.map((round) => {
        const points = round.guesses.filter((guess) => ids.has(guess.playerId)).map((guess) => guess.points);
        return points.length > 0 ? points.reduce((sum, value) => sum + value, 0) / points.length : null;
      });
      const last = averages.at(-1);
      return {
        team,
        players: members,
        total: Math.round(averages.reduce<number>((sum, average) => sum + (average ?? 0), 0)),
        lastRound: last === undefined || last === null ? null : Math.round(last),
      };
    })
    .sort((a, b) => b.total - a.total);
}
